
import { db } from '@/lib/firebase';
import { 
  doc, 
  setDoc, 
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction,
  query,
  where,
  collection,
  getDocs
} from 'firebase/firestore';
import { PAYMENT_PLANS, CREEM_CONFIG } from '@/config/appConfig';
import { UserTokens, PaymentHistory } from './types';

// Helper to get environment variables safely
const getEnvVar = (name: string, fallback: string = ''): string => {
  try {
    // Try to get from import.meta.env (Vite's way)
    if (import.meta?.env?.[name]) {
      return import.meta.env[name];
    }
    return fallback;
  } catch (e) {
    console.warn(`Error accessing env var ${name}`, e);
    return fallback;
  }
};

// Get Creem config from environment variables
const creemApiKey = getEnvVar('VITE_CREEM_API_KEY', CREEM_CONFIG.API_KEY);
const creemApiUrl = getEnvVar('VITE_CREEM_API_URL', CREEM_CONFIG.API_URL);

export const tokenService = {
  async verifyCreemPayment(orderId: string, userId: string): Promise<boolean> {
    console.log(`[PAYMENT] Verifying Creem payment for order: ${orderId}`);
    
    try {
      // First check if we've already processed this order
      const paymentsRef = collection(db, 'users', userId, 'payments');
      const q = query(paymentsRef, where('creemOrderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const payment = querySnapshot.docs[0].data();
        if (payment.status === 'completed') {
          console.log(`[PAYMENT] Order ${orderId} was already processed`);
          
          // Dispatch event even for previously processed orders
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('payment-success'));
            console.log('[PAYMENT] Dispatched payment-success event for previously processed order');
          }
          
          return true;
        }
      }

      // Record verification attempt
      const paymentId = `creem_${orderId}`;
      const paymentRef = doc(db, 'users', userId, 'payments', paymentId);
      
      await setDoc(paymentRef, {
        status: 'processing',
        creemOrderId: orderId,
        timestamp: serverTimestamp()
      }, { merge: true });

      try {
        // Verify with Creem API
        const response = await fetch(`${creemApiUrl}/verify/${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${creemApiKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to verify payment with Creem');
        }

        const paymentData = await response.json();
        
        // If payment is verified, record it and add tokens
        if (paymentData.status === 'completed') {
          // Find matching plan
          const plan = Object.entries(PAYMENT_PLANS).find(
            ([_, planDetails]) => planDetails.price === paymentData.amount
          )?.[0] as keyof typeof PAYMENT_PLANS;

          if (!plan) {
            throw new Error('Could not determine plan from payment amount');
          }

          // Add tokens
          await this.addTokens(userId, plan);
          
          // Update payment record
          await updateDoc(paymentRef, {
            status: 'completed',
            plan,
            amount: paymentData.amount,
            completedAt: serverTimestamp(),
            verificationResponse: paymentData
          });
          
          // Dispatch the event
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('payment-success'));
            console.log('[PAYMENT] Dispatched payment-success event after verification');
          }

          return true;
        }

        // Payment not completed
        await updateDoc(paymentRef, {
          status: 'failed',
          error: 'Payment not completed in Creem',
          failedAt: serverTimestamp()
        });
        
        return false;
      } catch (error) {
        // If verification fails, try force adding tokens
        console.error('[PAYMENT] Verification failed, attempting force add:', error);
        return this.forceAddTokens(userId, orderId);
      }
    } catch (error) {
      console.error('[PAYMENT] Payment verification failed:', error);
      return false;
    }
  },

  async createCheckout(plan: keyof typeof PAYMENT_PLANS, userId: string): Promise<string> {
    console.log(`[PAYMENT] Creating checkout for plan: ${plan}`);
    
    try {
      // Validate plan
      if (!PAYMENT_PLANS[plan]) {
        throw new Error('Invalid plan selected');
      }
      
      // Get direct checkout URL
      const checkoutUrl = PAYMENT_PLANS[plan].checkoutUrl;
      
      if (!checkoutUrl) {
        throw new Error('Checkout URL not configured for plan');
      }
      
      // Record the payment attempt with a unique ID
      const paymentId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      try {
        const paymentRef = doc(db, 'users', userId, 'payments', paymentId);
        await setDoc(paymentRef, {
          status: 'pending',
          plan,
          amount: PAYMENT_PLANS[plan].price,
          timestamp: serverTimestamp(),
          testMode: CREEM_CONFIG.TEST_MODE,
          paymentId
        });
      } catch (error) {
        console.error('[PAYMENT] Failed to record payment attempt:', error);
      }
      
      // Add payment ID and return URL to checkout URL
      const returnUrl = `${window.location.origin}/payment/success`;
      const urlWithParams = `${checkoutUrl}?payment_id=${paymentId}&return_url=${encodeURIComponent(returnUrl)}`;
      console.log(`[PAYMENT] Redirecting to checkout: ${urlWithParams}`);
      return urlWithParams;
    } catch (error) {
      console.error('[PAYMENT] Checkout error:', error);
      throw error;
    }
  },
  
  async addTokens(userId: string, plan: keyof typeof PAYMENT_PLANS): Promise<void> {
    console.log(`[PAYMENT] Adding tokens for plan ${plan} to user ${userId}`);
    
    const userTokenRef = doc(db, 'users', userId, 'tokens', 'balance');
    const userDoc = doc(db, 'users', userId);
    
    try {
      // Get current token balance
      const tokenDoc = await getDoc(userTokenRef);
      
      // Calculate renewal date
      const validityDays = PAYMENT_PLANS[plan].validity;
      const nextRenewal = new Date();
      nextRenewal.setDate(nextRenewal.getDate() + validityDays);
      
      // Prepare the token update data
      const tokenAmount = PAYMENT_PLANS[plan].tokens;
      const updateTime = serverTimestamp();
      
      // Use a transaction to ensure atomic updates
      await runTransaction(db, async (transaction) => {
        // Get the latest token document in the transaction
        const latestTokenDoc = await transaction.get(userTokenRef);
        
        if (!latestTokenDoc.exists()) {
          // Initialize new token record if it doesn't exist
          transaction.set(userTokenRef, {
            available: tokenAmount,
            used: 0,
            plan,
            lastPurchase: updateTime,
            nextRenewal: Timestamp.fromDate(nextRenewal),
            createdAt: updateTime,
            freeCreditsUsed: false,
            lastUpdated: updateTime
          });
        } else {
          // Update existing token record
          const currentBalance = latestTokenDoc.data().available || 0;
          transaction.update(userTokenRef, {
            available: currentBalance + tokenAmount,
            plan,
            lastPurchase: updateTime,
            nextRenewal: Timestamp.fromDate(nextRenewal),
            lastUpdated: updateTime
          });
        }
        
        // Update user document in the same transaction
        transaction.update(userDoc, {
          hasTokens: true,
          currentPlan: plan,
          lastPurchase: updateTime,
          lastActive: updateTime
        });
      });
      
      // Record successful payment
      const paymentId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const paymentRef = doc(db, 'users', userId, 'payments', paymentId);
      await setDoc(paymentRef, {
        status: 'completed',
        plan,
        amount: PAYMENT_PLANS[plan].price,
        tokensAdded: tokenAmount,
        timestamp: serverTimestamp(),
        completedAt: serverTimestamp()
      });
      
      console.log(`[PAYMENT] Successfully added ${tokenAmount} tokens to user ${userId}`);
      
      // Dispatch an event to notify of token update
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('payment-success'));
        console.log('[PAYMENT] Dispatched payment-success event');
      }
      
    } catch (error) {
      console.error('[PAYMENT] Failed to add tokens:', error);
      
      // Record failed transaction
      try {
        const paymentId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const paymentRef = doc(db, 'users', userId, 'payments', paymentId);
        await setDoc(paymentRef, {
          status: 'failed',
          plan,
          amount: PAYMENT_PLANS[plan].price,
          timestamp: serverTimestamp(),
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      } catch (paymentError) {
        console.error('[PAYMENT] Failed to record payment failure:', paymentError);
      }
      
      throw new Error(`Failed to add tokens: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
  
  async getTokenBalance(userId: string): Promise<UserTokens> {
    const tokenRef = doc(db, 'users', userId, 'tokens', 'balance');
    const tokenDoc = await getDoc(tokenRef);
    
    if (!tokenDoc.exists()) {
      // Initialize with default values if no balance exists
      const defaultBalance: UserTokens = {
        available: 0,
        used: 0,
        plan: 'free',
        lastPurchase: null,
        nextRenewal: null,
        createdAt: new Date()
      };
      
      await setDoc(tokenRef, defaultBalance);
      return defaultBalance;
    }
    
    const data = tokenDoc.data();
    
    // Convert Firestore Timestamps to Date objects if needed
    return {
      available: data.available || 0,
      used: data.used || 0,
      plan: data.plan || 'free',
      lastPurchase: data.lastPurchase,
      nextRenewal: data.nextRenewal,
      createdAt: data.createdAt,
      freeCreditsUsed: data.freeCreditsUsed || false
    };
  },
  
  async useTokens(userId: string, amount: number): Promise<boolean> {
    const tokenRef = doc(db, 'users', userId, 'tokens', 'balance');
    
    try {
      // Get current balance
      const tokenDoc = await getDoc(tokenRef);
      if (!tokenDoc.exists()) {
        throw new Error('No token balance found');
      }
      
      const currentBalance = tokenDoc.data().available || 0;
      if (currentBalance < amount) {
        return false; // Insufficient tokens
      }
      
      // Update balance
      await updateDoc(tokenRef, {
        available: increment(-amount),
        used: increment(amount)
      });
      
      return true;
    } catch (error) {
      console.error('[PAYMENT] Failed to use tokens:', error);
      throw error;
    }
  },

  async forceAddTokens(userId: string, orderId: string): Promise<boolean> {
    console.log(`[PAYMENT] Force adding tokens for order: ${orderId}`);
    
    try {
      // Check if order was already processed
      const paymentsRef = collection(db, 'users', userId, 'payments');
      const q = query(paymentsRef, where('creemOrderId', '==', orderId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const payment = querySnapshot.docs[0].data();
        if (payment.status === 'completed') {
          console.log(`[PAYMENT] Order ${orderId} was already processed successfully`);
          
          // Dispatch event even for previously processed orders
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('payment-success'));
          }
          
          return true;
        }
      }

      // Record payment attempt
      const paymentId = `creem_${orderId}`;
      const paymentRef = doc(db, 'users', userId, 'payments', paymentId);
      
      await setDoc(paymentRef, {
        status: 'processing',
        creemOrderId: orderId,
        timestamp: serverTimestamp(),
        retryCount: 0
      });

      // Determine amount from order ID (you'll need to implement this based on your Creem integration)
      const amount = '$5'; // Default to starter plan if can't determine
      
      // Find matching plan
      const plan = Object.entries(PAYMENT_PLANS).find(
        ([_, planDetails]) => planDetails.price === amount
      )?.[0] as keyof typeof PAYMENT_PLANS;

      if (!plan) {
        throw new Error('Could not determine plan from payment amount');
      }

      // Add tokens with retry mechanism
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          await this.addTokens(userId, plan);
          
          // Update payment record
          await updateDoc(paymentRef, {
            status: 'completed',
            plan,
            amount,
            completedAt: serverTimestamp()
          });
          
          return true;
        } catch (error) {
          console.error(`[PAYMENT] Retry ${retryCount + 1} failed:`, error);
          retryCount++;
          
          // Update retry count in payment record
          await updateDoc(paymentRef, {
            retryCount,
            lastError: error instanceof Error ? error.message : 'Unknown error',
            lastRetryAt: serverTimestamp()
          });
          
          if (retryCount === maxRetries) {
            // Final failure - update payment record
            await updateDoc(paymentRef, {
              status: 'failed',
              finalError: error instanceof Error ? error.message : 'Unknown error',
              failedAt: serverTimestamp()
            });
            throw error;
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, retryCount), 10000)));
        }
      }
      
      return false;
    } catch (error) {
      console.error('[PAYMENT] Force add tokens failed:', error);
      throw error;
    }
  }
};
